import { useMemo } from "react";
import { DataTable } from "@/components/ui/table/data-table";
import { Icons } from "@/components/icons";
import type { ColumnDef } from "@tanstack/react-table";
import { CellAction } from "./cell-action";
import { useAuthStore } from "@/store/auth";
import type { Opportunity, Pagination } from "@/services/types";

// Define the opportunity data type

interface UploadedOpportunitiesListProps {
  opportunities?: Opportunity[];
  pagination?: Pagination;
}

// Create columns definition
const createColumns = (
  activeOrganisationId?: string
): ColumnDef<Opportunity>[] => [
  {
    accessorKey: "title",
    header: "Opportunity Name",
    cell: ({ row }) => (
      <div className="font-medium text-gray-900">{row.getValue("title")}</div>
    ),
  },
  {
    accessorKey: "email",
    header: "Email address",
    cell: ({ row }) => {
      const ownerEntity = row.original.ownerEntity;
      const email = ownerEntity?.email || "-";
      return <div className="text-gray-600 line-clamp-2 max-w-md">{email}</div>;
    },
  },
  {
    accessorKey: "organisation",
    header: "Organisation",
    cell: ({ row }) => {
      const ownerEntity = row.original.ownerEntity;
      const orgName = ownerEntity?.name || "-";
      return <div className="text-gray-900">{orgName}</div>;
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      return <div className="text-gray-600 capitalize">{status || "-"}</div>;
    },
  },
  {
    accessorKey: "submissionsCount",
    header: "Submissions",
    cell: ({ row }) => {
      const count = row.getValue("submissionsCount") as number;
      return <div className="text-gray-600">{count ?? 0}</div>;
    },
  },
  {
    accessorKey: "createdAt",
    header: "Created",
    cell: ({ row }) => {
      const date = row.getValue("createdAt") as string;
      if (!date) return <div className="text-gray-600">-</div>;
      return (
        <div className="text-gray-600">
          {new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </div>
      );
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <CellAction
        data={row.original}
        activeOrganisationId={activeOrganisationId}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
];

const UploadedOpportunitiesList = ({
  opportunities = [],
  pagination,
}: UploadedOpportunitiesListProps) => {
  const { user } = useAuthStore();
  const activeOrganisationId = user?.organisation?.id;

  // Memoize columns and data to prevent unnecessary re-renders
  const columns = useMemo(
    () => createColumns(activeOrganisationId),
    [activeOrganisationId]
  );

  const totalItems = pagination?.totalDocs || opportunities.length;

  return (
    <div className="space-y-4 bg-white">
      <div className="px-4">
        <DataTable
          columns={columns}
          data={opportunities}
          totalItems={totalItems}
          emptyState={
            <div className="flex h-full w-full flex-col items-center justify-center">
              <Icons.emptyState />
              No opportunities found
            </div>
          }
        />
      </div>
    </div>
  );
};

export default UploadedOpportunitiesList;
